/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { TRUSTED_ORACLE_PROGRAM_ADDRESS } from '../programs';

export const FINALIZE_DISCRIMINATOR = new Uint8Array([
  171, 61, 218, 56, 127, 115, 12, 217,
]);

export function getFinalizeDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(FINALIZE_DISCRIMINATOR);
}

export type FinalizeInstruction<
  TProgram extends string = typeof TRUSTED_ORACLE_PROGRAM_ADDRESS,
  TAccountOracleAuthority extends string | AccountMeta<string> = string,
  TAccountOracleState extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountOracleAuthority extends string
        ? ReadonlySignerAccount<TAccountOracleAuthority> &
            AccountSignerMeta<TAccountOracleAuthority>
        : TAccountOracleAuthority,
      TAccountOracleState extends string
        ? WritableAccount<TAccountOracleState>
        : TAccountOracleState,
      ...TRemainingAccounts,
    ]
  >;

export type FinalizeInstructionData = {
  discriminator: ReadonlyUint8Array;
  winningMint: Address;
};

export type FinalizeInstructionDataArgs = { winningMint: Address };

export function getFinalizeInstructionDataEncoder(): FixedSizeEncoder<FinalizeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['winningMint', getAddressEncoder()],
    ]),
    (value) => ({ ...value, discriminator: FINALIZE_DISCRIMINATOR }),
  );
}

export function getFinalizeInstructionDataDecoder(): FixedSizeDecoder<FinalizeInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['winningMint', getAddressDecoder()],
  ]);
}

export function getFinalizeInstructionDataCodec(): FixedSizeCodec<
  FinalizeInstructionDataArgs,
  FinalizeInstructionData
> {
  return combineCodec(
    getFinalizeInstructionDataEncoder(),
    getFinalizeInstructionDataDecoder(),
  );
}

export type FinalizeInput<
  TAccountOracleAuthority extends string = string,
  TAccountOracleState extends string = string,
> = {
  /** The oracle authority - must sign to finalize */
  oracleAuthority: TransactionSigner<TAccountOracleAuthority>;
  oracleState: Address<TAccountOracleState>;
  winningMint: FinalizeInstructionDataArgs['winningMint'];
};

export function getFinalizeInstruction<
  TAccountOracleAuthority extends string,
  TAccountOracleState extends string,
  TProgramAddress extends Address = typeof TRUSTED_ORACLE_PROGRAM_ADDRESS,
>(
  input: FinalizeInput<TAccountOracleAuthority, TAccountOracleState>,
  config?: { programAddress?: TProgramAddress },
): FinalizeInstruction<
  TProgramAddress,
  TAccountOracleAuthority,
  TAccountOracleState
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? TRUSTED_ORACLE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    oracleAuthority: {
      value: input.oracleAuthority ?? null,
      isWritable: false,
    },
    oracleState: { value: input.oracleState ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('oracleAuthority', accounts.oracleAuthority),
      getAccountMeta('oracleState', accounts.oracleState),
    ],
    data: getFinalizeInstructionDataEncoder().encode(
      args as FinalizeInstructionDataArgs,
    ),
    programAddress,
  } as FinalizeInstruction<
    TProgramAddress,
    TAccountOracleAuthority,
    TAccountOracleState
  >);
}

export type ParsedFinalizeInstruction<
  TProgram extends string = typeof TRUSTED_ORACLE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The oracle authority - must sign to finalize */
    oracleAuthority: TAccountMetas[0];
    oracleState: TAccountMetas[1];
  };
  data: FinalizeInstructionData;
};

export function parseFinalizeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedFinalizeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      oracleAuthority: getNextAccount(),
      oracleState: getNextAccount(),
    },
    data: getFinalizeInstructionDataDecoder().decode(instruction.data),
  };
}
